import { cn } from '@/lib/utils';
import { MessageSquare, Mail, Smartphone, TrendingUp, TrendingDown } from 'lucide-react';

interface ReguaPerformance {
  ruleId: string;
  ruleName: string;
  invoicesSent: number;
  invoicesPaid: number;
  paymentRate: number;
  overdueInvoices: number;
  defaultRate: number;
  avgPaymentDays: number;
  topChannel: 'whatsapp' | 'email' | 'sms';
  engagementRate: number;
  isActive: boolean;
}

const mockData: ReguaPerformance[] = [
  {
    ruleId: '1',
    ruleName: 'Cobrança Inicial (0-5 dias)',
    invoicesSent: 450,
    invoicesPaid: 315,
    paymentRate: 70,
    overdueInvoices: 45,
    defaultRate: 10,
    avgPaymentDays: 3,
    topChannel: 'whatsapp',
    engagementRate: 92,
    isActive: true,
  },
  {
    ruleId: '2',
    ruleName: 'Cobrança Intensiva (6-14 dias)',
    invoicesSent: 280,
    invoicesPaid: 168,
    paymentRate: 60,
    overdueInvoices: 84,
    defaultRate: 30,
    avgPaymentDays: 10,
    topChannel: 'email',
    engagementRate: 75,
    isActive: true,
  },
  {
    ruleId: '3',
    ruleName: 'Cobrança Judicial (15+ dias)',
    invoicesSent: 120,
    invoicesPaid: 60,
    paymentRate: 50,
    overdueInvoices: 60,
    defaultRate: 50,
    avgPaymentDays: 22,
    topChannel: 'sms',
    engagementRate: 58,
    isActive: true,
  },
];

const channelIcons = {
  whatsapp: MessageSquare,
  email: Mail,
  sms: Smartphone,
};

const channelColors = {
  whatsapp: 'bg-[hsl(142,70%,90%)] text-[hsl(142,70%,30%)]',
  email: 'bg-[hsl(220,70%,95%)] text-[hsl(220,70%,40%)]',
  sms: 'bg-[hsl(280,70%,95%)] text-[hsl(280,70%,40%)]',
};

function getRateColor(rate: number, isPayment: boolean) {
  if (isPayment) {
    if (rate >= 60) return 'text-[hsl(var(--accent-600))]';
    if (rate >= 40) return 'text-[hsl(40,95%,40%)]';
    return 'text-[hsl(var(--careful-600))]';
  } else {
    if (rate <= 15) return 'text-[hsl(var(--accent-600))]';
    if (rate <= 30) return 'text-[hsl(40,95%,40%)]';
    return 'text-[hsl(var(--careful-600))]';
  }
}

export function ReguasPerformanceTable() {
  return (
    <div className="p-5 rounded-2xl border bg-card hover:shadow-lg transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Performance das Réguas</h3>
          <p className="text-xs text-muted-foreground">Comparativo de efetividade por régua de cobrança</p>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Régua</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Enviadas</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tx. Pagamento</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tx. Inadimplência</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Canal Top</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tempo Médio</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Engajamento</th>
            </tr>
          </thead>
          <tbody>
            {mockData.map((regua, index) => {
              const ChannelIcon = channelIcons[regua.topChannel];
              return (
                <tr 
                  key={regua.ruleId}
                  className={cn(
                    'border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer',
                    index === mockData.length - 1 && 'border-b-0'
                  )}
                >
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'w-2 h-2 rounded-full flex-shrink-0',
                        regua.isActive ? 'bg-[hsl(var(--accent-500))]' : 'bg-muted-foreground'
                      )} />
                      <span className="font-medium text-foreground truncate max-w-[200px]">
                        {regua.ruleName}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className="text-foreground font-medium">
                      {regua.invoicesSent.toLocaleString('pt-BR')}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className={cn('font-bold', getRateColor(regua.paymentRate, true))}>
                        {regua.paymentRate}%
                      </span>
                      {regua.paymentRate >= 60 ? (
                        <TrendingUp className="h-3 w-3 text-[hsl(var(--accent-500))]" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-[hsl(var(--careful-500))]" />
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className={cn('font-bold', getRateColor(regua.defaultRate, false))}>
                      {regua.defaultRate}%
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                      channelColors[regua.topChannel]
                    )}>
                      <ChannelIcon className="h-3 w-3" />
                      {regua.topChannel === 'whatsapp' ? 'WhatsApp' : 
                       regua.topChannel === 'email' ? 'Email' : 'SMS'}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className="text-foreground">{regua.avgPaymentDays} dias</span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[hsl(var(--basic-500))] rounded-full transition-all"
                          style={{ width: `${regua.engagementRate}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">{regua.engagementRate}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
